import {
  DeveloperPermissionItem,
  PermissionItem,
} from '../constants/permission'
import { DeveloperPermissionItem as Dev } from '../constants/permission/PermissionItem'
import { listStringify } from '../utils/listStringify'
import {
  _AuthorizedOptions,
  Authorized,
  AuthUserProperties,
  CreateAuthUserOptions,
  ImplicitlyAuthorizedOptions,
  ImplicitlyAuthorizedWithProjectOptions,
  UserProjectAuth,
} from './types'

/**
 * used for the Auth flow
 *
 * shares many properties with the User entity in the server
 */
export class AuthUser {
  /**
   * unwraps an object which extends {@link Authorized} and throws an error with
   * the unauthorized reason
   *
   * if authorized, returns the authorized object
   *
   * @param authorized a promise of an object that extends {@link Authorized}
   * @param error an optional Error ClassType or callback to return an instance of the Error with unauthorized properties
   */
  static authorizeOrThrow<T extends Authorized, U extends Error>(
    authorized: T,
    error?:
      | { new (message: string, ...rest: any[]): U }
      | ((unauthorized: T & { authorized: false }) => U)
  ) {
    if (!authorized.authorized) {
      const u = error ?? Error
      let err
      try {
        err = new (u as any)(authorized.reason)
      } catch {
        err = (u as any)(authorized)
      }
      throw err
    }
    return authorized as T & { authorized: true }
  }

  /**
   * creates a new instance of an {@link AuthUser} using defaults for missing
   * values
   *
   * @param options {@link CreateAuthUserOptions}
   */
  static create({
    developer = false,
    projectAuth = {},
    displayName = undefined,
    givenName = undefined,
    surname = undefined,
    ...rest
  }: CreateAuthUserOptions) {
    return new AuthUser({
      ...rest,
      developer,
      projectAuth,
      displayName,
      givenName,
      surname,
    })
  }
  private constructor(props: AuthUserProperties) {
    this.id = props.id
    this.mail = props.mail
    this.username = props.username
    this.developer = props.developer
    this.displayName = props.displayName
    this.givenName = props.givenName
    this.surname = props.surname
    this.projectAuth = props.projectAuth
  }
  /**
   * a plain old javascript object with all properties necessary to instantiate
   * an {@link AuthUser}
   */
  get properties(): AuthUserProperties {
    return {
      developer: this.developer,
      displayName: this.displayName,
      givenName: this.givenName,
      id: this.id,
      mail: this.mail,
      projectAuth: this.projectAuth,
      surname: this.surname,
      username: this.username,
    }
  }
  displayName: string | undefined
  givenName: string | undefined
  surname: string | undefined
  username: string
  mail: string
  id: string
  /**
   * used to grant/revoke access to parts of the UI and API for all projects
   *
   * if true they have full access to everything
   *
   * if an array use project permissions to check for permission
   *
   * @see {@link DeveloperPermissionItem}
   */
  developer: boolean | DeveloperPermissionItem['id'][]
  /**
   * used to get the user's roles and permission for a specific project
   *
   * @see {@link UserProjectAuth}
   */
  projectAuth: Record<string, UserProjectAuth>

  private toSet<T extends PermissionItem['id']>(values: T[] = []) {
    return values.reduce((set, item) => set.add(item), new Set<T>())
  }

  /**
   * reduces {@link developer} into a set
   *
   * __if developer is `true` the set will be empty__
   */
  get devs() {
    return this.toSet(typeof this.developer === 'boolean' ? [] : this.developer)
  }

  private get notAuthorizedPrefix() {
    return `${this.username} is not authorized: ` as const
  }

  /**
   * @see {@link notAuthorizedPrefix}
   */
  private notAuthorized(message: string) {
    return {
      authorized: false as const,
      reason: `${this.notAuthorizedPrefix}${message}` as const,
    }
  }

  /**
   * performs authorization logic
   *
   * 1. authorized if {@link developer} is `true`
   * 2. authorized if developer permission present in {@link developer}
   * 3. authorized if basic permission present in {@link projectAuth} or project admin
   */
  private _authorized({
    project,
    to,
    devs = this.devs,
    projectAuth = this.projectAuth,
  }: _AuthorizedOptions): Authorized {
    if (this.developer === true) return { authorized: true }

    const unauthorized = this.notAuthorized(to.name)

    if (to instanceof Dev) {
      if (!devs.has(to.id)) return unauthorized
      return { authorized: true }
    }

    if (!project) return this.notAuthorized('project null or not defined')
    const projectId = typeof project === 'string' ? project : project.globalId
    const got = projectAuth[projectId]?.permissions ?? []
    if (got !== true && !got.includes(to.id)) return unauthorized

    return { authorized: true }
  }

  /**
   * returns whether this user is a full developer as an {@link Authorized} object
   */
  isFullDeveloper(): Authorized {
    if (this.developer === true) return { authorized: true }
    return this.notAuthorized('not a full developer')
  }

  /**
   * returns whether this user is a project admin for the supplied projectId as
   * an {@link Authorized} object
   *
   * full developers are project admins
   *
   * @param projectId
   * @see {@link projectAuth}
   */
  admin(projectId: string | null | undefined): Authorized {
    if (!projectId) return this.notAuthorized('projectId null or not defined')
    const got = this.projectAuth[projectId]?.permissions
    return got === true || this.developer === true
      ? { authorized: true }
      : this.notAuthorized(`not an admin for ${projectId}`)
  }

  /**
   * returns whether this user is authorized to perform the action associated
   * with the permission for the supplied project
   *
   * @param to a permission to check
   * @param project a Project.globalId or Project object
   */
  authorized(
    to: PermissionItem,
    project: { globalId: string } | string | undefined | null
  ): Authorized
  /**
   * returns whether this user is authorized to perform the action associated
   * with the permission
   *
   * @param to a developer permission to check
   */
  authorized(to: DeveloperPermissionItem): Authorized
  // implementation
  authorized(
    to: PermissionItem,
    project?: { globalId: string } | string | null
  ) {
    return this._authorized({ to, project })
  }

  /**
   * collapses a list of permission items into a list of permission names which
   * are not authorized
   *
   * @see {@link _authorized}
   */
  private names(
    to: PermissionItem[],
    project?: { globalId: string } | string | null
  ) {
    const devs = this.devs
    const projectAuth = this.projectAuth
    return to.reduce((reasons, to) => {
      const a = this._authorized({ to, project, devs, projectAuth })
      if (a.authorized) return reasons
      const [_, name] = a.reason.split(this.notAuthorizedPrefix)
      reasons.push(name)
      return reasons
    }, Array<string>())
  }

  /**
   * returns whether this user is authorized to perform every action associated
   * with the permissions for the supplied project
   *
   * __no permissions means not authorized__
   *
   * @param to permissions to check
   * @param project a Project.globalId or Project object
   */
  everyAuthorized(
    to: PermissionItem[],
    project: { globalId: string } | string | undefined | null
  ): Authorized
  /**
   * returns whether this user is authorized to perform every action associated
   * with the permissions
   *
   * __no permissions means not authorized__
   *
   * @param to developer permissions to check
   */
  everyAuthorized(to: DeveloperPermissionItem[]): Authorized
  // implementation
  everyAuthorized(
    to: PermissionItem[],
    project?: { globalId: string } | string | null
  ): Authorized {
    if (!to.length)
      return {
        authorized: false,
        reason: this.notAuthorizedPrefix + '!to.length',
      }
    const names = this.names(to, project)
    /**
     * if there are no reasons... all are authorized
     */
    if (!names.length) return { authorized: true }
    return {
      authorized: false,
      reason: this.notAuthorizedPrefix + listStringify({ items: names }),
    }
  }

  /**
   * returns whether this user is authorized to perform some actions associated
   * with the permissions for the supplied project
   *
   * __no permissions means authorized__
   *
   * @param to permissions to check
   * @param project a Project.globalId or Project object
   */
  someAuthorized(
    to: PermissionItem[],
    project: { globalId: string } | string | undefined | null
  ): Authorized
  /**
   * returns whether this user is authorized to perform some actions associated
   * with the permissions
   *
   * __no permissions means authorized__
   *
   * @param to developer permissions to check
   */
  someAuthorized(to: DeveloperPermissionItem[]): Authorized
  // implementation
  someAuthorized(
    to: PermissionItem[],
    project?: { globalId: string } | string | null
  ): Authorized {
    if (!to.length) return { authorized: true }
    const names = this.names(to, project)
    /**
     * if there are less reasons than permissions... some are authorized
     */
    if (names.length < to.length) return { authorized: true }
    return {
      authorized: false,
      reason: this.notAuthorizedPrefix + listStringify({ items: names }),
    }
  }

  /**
   * returns whether this user is implicitly authorized through membership in a
   * list of users or list of roles with users
   *
   * this __cannot__ check if a user is authorized by being an admin. you should
   * try to use the overload that uses {@link ImplicitlyAuthorizedWithProjectOptions}
   * or make sure you're accounting for project admin status as well
   *
   * @param options {@link ImplicitlyAuthorizedOptions}
   */
  implicitlyAuthorized(options: ImplicitlyAuthorizedOptions): Authorized
  /**
   * returns whether this user is implicitly authorized as a project admin, or
   * through membership in a list of users or list of roles with users
   *
   * @param options {@link ImplicitlyAuthorizedWithProjectOptions}
   */
  implicitlyAuthorized(
    options: ImplicitlyAuthorizedWithProjectOptions
  ): Authorized
  // implementation
  implicitlyAuthorized({
    roles = [],
    users = [],
    ...options
  }:
    | ImplicitlyAuthorizedOptions
    | ImplicitlyAuthorizedWithProjectOptions): Authorized {
    const ids = new Set([
      ...roles.reduce((ids, role) => {
        role?.users?.forEach((user) => {
          if (user) ids.push(user.id)
        })
        return ids
      }, Array<string>()),
      ...users.reduce((ids, user) => {
        if (user) ids.push(user.id)
        return ids
      }, Array<string>()),
    ])
    if (this.developer === true) return { authorized: true }
    if ('projectId' in options && this.admin(options.projectId).authorized)
      return { authorized: true }
    if (ids.has(this.id)) return { authorized: true }
    return {
      authorized: false,
      reason: `${this.username} is not implicitly authorized: not a member of users or roles`,
    }
  }
}
